import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import { IssueService } from "./issueService";
import { getTripGroupsUsers } from "../trips/tripGroupService";
import { Modal } from '@flightstats/airframe-react';
import { modalContentStyle } from "../components/modal/Modal";
import { BatchAssignProps } from './types';
import { Agent } from "../headersegment/interfaces";

const SelectAssignee: React.FC<{
  assignTo: string | null;
  assignToOptions: Array<{ name: string; value: string }>;
  setAssignTo: (value: string) => void;
}> = ({ assignTo, assignToOptions, setAssignTo }) => (
  <div className="row">
    <select
      size={10}
      className="form-control btn btn-default dropdown-toggle"
      value={assignTo || ""}
      onChange={(e) => setAssignTo(e.target.value)}
      required
    >
      {assignToOptions.map((option, index) => (
        <option key={index} value={option.value}>
          {option.name}
        </option>
      ))}
    </select>
  </div>
);



const MessageDisplay: React.FC<{ message: string }> = ({ message }) => (
  <div className="row">
    <div className="col-md-2"></div>
    <div className="col-md-8 center">{message}</div>
    <div className="col-md-2"></div>
  </div>
);

const BatchAssign: React.FC<BatchAssignProps> = ({ issues, cancel, issuesUpdated, loadSpinner }) => {
  const [loadingView, setLoadingView] = useState(true);
  const [canSelectAssignee, setCanSelectAssignee] = useState(false);
  const [unableToSelectMessage, setUnableToSelectMessage] = useState("Unable to get a list of agents at this time.");
  const [assignToOptions, setAssignToOptions] = useState<Array<{ name: string; value: string }>>([]);
  const [assignTo, setAssignTo] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [formValid, setFormValid] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const tripGroupIds: any = _.chain(issues)
      .map("tripGroupId")
      .filter((tripGroupId) => tripGroupId != null)
      .uniq()
      .value();
    loadSpinner(true);
    getTripGroupsUsers({tripGroupIds})
      .then((resp) => {
        loadSpinner(false);
        if (isMounted) {
          if (resp?.status === 200 && resp?.data?.length) {
            const options = resp.data.filter((assignLists: { userFullObj?: { active: boolean } }) => assignLists?.userFullObj?.active).map((agent: Agent) => ({
              name: agent.id,
              value: agent.user.aav1UserId,
            }));
            setAssignToOptions(options);
            setAssignTo(options[0]?.value);
            setCanSelectAssignee(true);
          } else {
            setUnableToSelectMessage("No agents are available to select from.");
          }
        }
      })
      .catch(() => {
        loadSpinner(false);
        if (isMounted) {
          setUnableToSelectMessage("Unable to get a list of agents at this time.");
        }
      })
      .finally(() => {
        loadSpinner(false);
        if (isMounted) {
          setLoadingView(false);
        }
      });

    return () => {
      loadSpinner(false);
      isMounted = false;
    };
  }, [issues]);

  const ok = useCallback(() => {
    setSubmitted(true);
    if (assignTo) {
      IssueService.assign(issues, assignTo, true, issuesUpdated);
    }
    setIsOpen(false);
    cancel();
  }, [assignTo, issues, cancel]);

  return (
    <Modal isOpen={isOpen} contentStyle={modalContentStyle}>
      <div className="issue-assign issue-resolution-modal modal-dialog modal-sm">
        <form role="form" onSubmit={ok} noValidate>
          <div className="title">
            ASSIGN ISSUES TO
            <a onClick={cancel}>
              <i id="modalCancelled" className="cancel pull-right glyphicon icon-white glyphicon-remove"></i>
            </a>
          </div>

          <div className="content">
            <div className="choices">
              {!loadingView &&
                (canSelectAssignee ? (
                  <SelectAssignee assignTo={assignTo} assignToOptions={assignToOptions} setAssignTo={setAssignTo} />
                ) : (
                  <MessageDisplay message={unableToSelectMessage} />
                ))}
            </div>

            <div className="action">
              <button
                id="modalSubmitted"
                className="btn btn-default button-action"
                type="submit"
                disabled={formValid || submitted}
              >
                ASSIGN
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default BatchAssign;